import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Typography, Slider, Stack, useTheme } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MusicNoteIcon from '@mui/icons-material/MusicNote';

interface AudioPlayerProps {
    src: string;
    title?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, title }) => {
    const theme = useTheme();
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);
    const volumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const setAudioData = () => {
            setDuration(audio.duration);
            setCurrentTime(audio.currentTime);
        };

        const setAudioTime = () => setCurrentTime(audio.currentTime);

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleEnded = () => setIsPlaying(false);

        audio.addEventListener('loadeddata', setAudioData);
        audio.addEventListener('timeupdate', setAudioTime);
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('loadeddata', setAudioData);
            audio.removeEventListener('timeupdate', setAudioTime);
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handleEnded);

            // Cleanup volume timeout
            if (volumeTimeoutRef.current) {
                clearTimeout(volumeTimeoutRef.current);
            }
        };
    }, []);

    const togglePlayPause = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play().catch((error) => {
                console.error('Error playing audio:', error);
                setIsPlaying(false);
            });
        }
    };

    const handleProgressChange = (event: Event, newValue: number | number[]) => {
        const audio = audioRef.current;
        if (!audio) return;

        const value = newValue as number;
        audio.currentTime = value;
        setCurrentTime(value);
    };

    const handleVolumeChange = (event: Event, newValue: number | number[]) => {
        const audio = audioRef.current;
        if (!audio) return;

        const value = newValue as number;
        audio.volume = value;
        setVolume(value);
        setIsMuted(value === 0);
    };

    const toggleMute = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isMuted) {
            audio.volume = volume;
            setIsMuted(false);
        } else {
            audio.volume = 0;
            setIsMuted(true);
        }
    };

    const formatTime = (time: number) => {
        if (isNaN(time)) return '0:00';

        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const progress = duration ? (currentTime / duration) * 100 : 0;

    const handleVolumeMouseEnter = () => {
        if (volumeTimeoutRef.current) {
            clearTimeout(volumeTimeoutRef.current);
        }
        setShowVolumeSlider(true);
    };

    const handleVolumeMouseLeave = () => {
        volumeTimeoutRef.current = setTimeout(() => {
            setShowVolumeSlider(false);
        }, 300); // 300ms delay before hiding
    };

    return (
        <Box
            sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}08)`,
                border: `1px solid ${theme.palette.primary.main}30`,
                borderRadius: '16px',
                padding: '16px',
                margin: '8px -5px',
                width: 'calc(100% + 10px)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}20 0%, transparent 100%)`,
                    transform: `translateX(${progress - 100}%)`,
                    transition: 'transform 0.1s linear',
                    zIndex: 0,
                },
            }}
        >
            <audio ref={audioRef} src={src} />

            <Stack
                direction='row'
                alignItems='center'
                spacing={2}
                sx={{ position: 'relative', zIndex: 1 }}
            >
                {/* Music Icon */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: `${theme.palette.primary.main}20`,
                        border: `1px solid ${theme.palette.primary.main}40`,
                    }}
                >
                    <MusicNoteIcon
                        sx={{
                            color: theme.palette.primary.main,
                            fontSize: 24,
                        }}
                    />
                </Box>

                {/* Main Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Title */}
                    <Typography
                        variant='body2'
                        sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            marginBottom: '4px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {title}
                    </Typography>

                    {/* Progress Bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            variant='caption'
                            sx={{ minWidth: '35px', fontSize: '0.75rem' }}
                        >
                            {formatTime(currentTime)}
                        </Typography>

                        <Slider
                            size='small'
                            value={currentTime}
                            max={duration}
                            onChange={handleProgressChange}
                            sx={{
                                color: theme.palette.primary.main,
                                height: 6,
                                '& .MuiSlider-track': {
                                    border: 'none',
                                    height: 6,
                                    borderRadius: 3,
                                },
                                '& .MuiSlider-rail': {
                                    opacity: 0.3,
                                    backgroundColor: theme.palette.text.disabled,
                                    height: 6,
                                    borderRadius: 3,
                                },
                                '& .MuiSlider-thumb': {
                                    height: 16,
                                    width: 16,
                                    backgroundColor: theme.palette.primary.main,
                                    border: `2px solid ${theme.palette.background.paper}`,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible':
                                        {
                                            boxShadow: `0 0 0 8px ${theme.palette.primary.main}20`,
                                        },
                                },
                            }}
                        />

                        <Typography
                            variant='caption'
                            sx={{ minWidth: '35px', fontSize: '0.75rem' }}
                        >
                            {formatTime(duration)}
                        </Typography>
                    </Box>
                </Box>

                {/* Controls */}
                <Stack direction='row' alignItems='center' spacing={0.5}>
                    {/* Volume Control */}
                    <Box
                        sx={{ position: 'relative' }}
                        onMouseEnter={handleVolumeMouseEnter}
                        onMouseLeave={handleVolumeMouseLeave}
                    >
                        <IconButton
                            onClick={toggleMute}
                            size='small'
                            sx={{
                                color: theme.palette.secondary.main,
                                '&:hover': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: `${theme.palette.primary.main}10`,
                                },
                            }}
                        >
                            {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                        </IconButton>

                        {showVolumeSlider && (
                            <Box
                                onMouseEnter={handleVolumeMouseEnter}
                                onMouseLeave={handleVolumeMouseLeave}
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: '100%',
                                    transform: 'translateY(-50%)',
                                    backgroundColor: theme.palette.background.paper,
                                    borderRadius: '8px',
                                    padding: '8px 4px',
                                    boxShadow: theme.shadows[8],
                                    border: `1px solid ${theme.palette.divider}`,
                                    marginRight: '8px',
                                    zIndex: 10,
                                }}
                            >
                                <Slider
                                    orientation='vertical'
                                    value={isMuted ? 0 : volume}
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    onChange={handleVolumeChange}
                                    sx={{
                                        color: theme.palette.primary.main,
                                        height: 60,
                                        '& .MuiSlider-thumb': {
                                            width: 16,
                                            height: 16,
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Play/Pause Button */}
                    <IconButton
                        onClick={togglePlayPause}
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            width: 44,
                            height: 44,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark,
                                transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s ease-in-out',
                            boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                        }}
                    >
                        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                </Stack>
            </Stack>
        </Box>
    );
};

export default AudioPlayer;
